import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, IconButton, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight, Fullscreen, FullscreenExit } from '@mui/icons-material';
import api from '../../services/api';
import './SeancePresentation.css';

function SeancePresentation() {
  const { seanceId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [seance, setSeance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slideContentRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const isScrollingRef = useRef(false);
  const lastScrollTimeRef = useRef(0);
  const scrollThreshold = 80; // Increased threshold to ensure user has scrolled enough
  
  // Color palette
  const colors = {
    primary: '#3498db',
    secondary: '#9b59b6',
    accent: '#e74c3c',
    success: '#2ecc71',
    warning: '#f39c12',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    cardBg: '#ffffff',
    textPrimary: '#2c3e50',
    textSecondary: '#7f8c8d',
    quizCorrect: '#2ecc71',
    quizIncorrect: '#ecf0f1',
  };

  useEffect(() => {
    const fetchSeance = async () => {
      try {
        setLoading(true);
        const response = await api.seance.getPublicSeanceById(seanceId);
        
        // Extract seance data from the nested response structure
        const seanceData = response.data?.seance || response;
        setSeance(seanceData);
        
        // Build slides from all blocks
        const allSlides = [];
        seanceData.sequences?.forEach((sequence, sequenceIndex) => {
          const sequenceNumber = sequenceIndex + 1;
          const sequenceTitle = `Séquence ${sequenceNumber} - ${sequence.subTheme || sequence.title || ''}`;
          sequence.blocs?.forEach((block) => {
            allSlides.push({ block, sequenceTitle });
          });
        });
        setSlides(allSlides);
      } catch (error) {
        console.error('Error fetching seance:', error);
      } finally {
        setLoading(false);
      }
    };

    if (seanceId) {
      fetchSeance();
    }
  }, [seanceId]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        handleNext();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        handlePrevious();
      } else if (e.key === 'Escape') {
        if (isFullscreen) {
          exitFullscreen();
        }
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentSlide, slides.length, isFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Reset scroll position when slide changes with smooth transition
  useEffect(() => {
    if (slideContentRef.current) {
      // Smooth scroll to top
      slideContentRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [currentSlide]);

  // Prevent body scroll when presentation is active (desktop only)
  useEffect(() => {
    if (!isMobile) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isMobile]);

  // Handle scroll for mobile - change slides on scroll
  useEffect(() => {
    if (!isMobile) return;

    const handleWheel = (e) => {
      if (isScrollingRef.current) return;
      
      const slideElement = slideContentRef.current;
      if (!slideElement) return;

      const now = Date.now();
      // Prevent rapid successive scroll events
      if (now - lastScrollTimeRef.current < 100) return;
      lastScrollTimeRef.current = now;

      const { scrollTop, scrollHeight, clientHeight } = slideElement;
      const scrollRemaining = scrollHeight - scrollTop - clientHeight;
      const isAtTop = scrollTop <= 5; // Small tolerance for top
      const isAtBottom = scrollRemaining <= scrollThreshold; // Larger threshold

      // Scroll down
      if (e.deltaY > 0) {
        if (isAtBottom && scrollRemaining < 20) {
          // Only change if really at bottom and user continues scrolling
          e.preventDefault();
          if (currentSlide < slides.length - 1) {
            isScrollingRef.current = true;
            // Smooth transition
            slideElement.style.transition = 'opacity 0.3s ease';
            slideElement.style.opacity = '0.7';
            
            setTimeout(() => {
              slideElement.scrollTop = 0;
              setCurrentSlide(prev => prev + 1);
              setTimeout(() => {
                slideElement.style.opacity = '1';
                slideElement.style.transition = '';
                isScrollingRef.current = false;
              }, 300);
            }, 150);
          }
        }
      } 
      // Scroll up
      else if (e.deltaY < 0) {
        if (isAtTop) {
          // At top, go to previous slide
          e.preventDefault();
          if (currentSlide > 0) {
            isScrollingRef.current = true;
            // Smooth transition
            slideElement.style.transition = 'opacity 0.3s ease';
            slideElement.style.opacity = '0.7';
            
            setTimeout(() => {
              setCurrentSlide(prev => prev - 1);
              setTimeout(() => {
                if (slideContentRef.current) {
                  slideContentRef.current.scrollTop = slideContentRef.current.scrollHeight;
                }
                slideElement.style.opacity = '1';
                slideElement.style.transition = '';
                isScrollingRef.current = false;
              }, 300);
            }, 150);
          }
        }
      }
    };

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      const startY = touch.clientY;
      let startScrollTop = 0;
      
      if (slideContentRef.current) {
        startScrollTop = slideContentRef.current.scrollTop;
      }

      const handleTouchMove = (e) => {
        if (isScrollingRef.current) return;
        
        const touch = e.touches[0];
        const deltaY = touch.clientY - startY;
        const slideElement = slideContentRef.current;
        
        if (!slideElement) return;

        const { scrollTop, scrollHeight, clientHeight } = slideElement;
        const scrollRemaining = scrollHeight - scrollTop - clientHeight;
        const isAtTop = scrollTop <= 5;
        const isAtBottom = scrollRemaining <= scrollThreshold;

        // Swipe down (scroll up) - need stronger swipe and be at top
        if (deltaY > 80 && isAtTop && currentSlide > 0) {
          e.preventDefault();
          isScrollingRef.current = true;
          // Smooth transition
          slideElement.style.transition = 'opacity 0.3s ease';
          slideElement.style.opacity = '0.7';
          
          setTimeout(() => {
            setCurrentSlide(prev => prev - 1);
            setTimeout(() => {
              if (slideContentRef.current) {
                slideContentRef.current.scrollTop = slideContentRef.current.scrollHeight;
              }
              slideElement.style.opacity = '1';
              slideElement.style.transition = '';
              isScrollingRef.current = false;
            }, 300);
          }, 150);
          
          document.removeEventListener('touchmove', handleTouchMove);
          document.removeEventListener('touchend', handleTouchEnd);
        }
        // Swipe up (scroll down) - need stronger swipe and be at bottom
        else if (deltaY < -80 && isAtBottom && scrollRemaining < 20 && currentSlide < slides.length - 1) {
          e.preventDefault();
          isScrollingRef.current = true;
          // Smooth transition
          slideElement.style.transition = 'opacity 0.3s ease';
          slideElement.style.opacity = '0.7';
          
          setTimeout(() => {
            slideElement.scrollTop = 0;
            setCurrentSlide(prev => prev + 1);
            setTimeout(() => {
              slideElement.style.opacity = '1';
              slideElement.style.transition = '';
              isScrollingRef.current = false;
            }, 300);
          }, 150);
          
          document.removeEventListener('touchmove', handleTouchMove);
          document.removeEventListener('touchend', handleTouchEnd);
        }
      };

      const handleTouchEnd = () => {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };

      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    };

    const container = document.querySelector('.presentation-container');
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      container.addEventListener('touchstart', handleTouchStart, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
        container.removeEventListener('touchstart', handleTouchStart);
      }
    };
  }, [isMobile, currentSlide, slides.length]);

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  };

  const getFirstValidatedImage = (block) => {
    const images = block.bloc_data?.images || block.content?.images || [];
    const validatedImage = images.find(img => img.status_seance === 'validated');
    return validatedImage?.compressed_url || validatedImage?.img_url || validatedImage?.url || null;
  };

  const getBlockContent = (block) => {
    const blockType = block.type_bloc || 'unknown';
    
    switch (blockType) {
      case 'quiz':
      case 'sondage': {
        const question = block.bloc_data?.question || block.content?.question || '';
        const answers = block.bloc_data?.answers || block.content?.answers || [];
        
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {question && (
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold', 
                  color: '#FF6B35', // Warm orange/coral
                  mb: 1.5,
                  fontSize: '1.1rem'
                }}>
                  Question:
                </Typography>
                <Typography variant="h6" sx={{ 
                  color: '#2C3E50', // Dark blue-gray
                  fontWeight: 500,
                  fontSize: '1.125rem'
                }}>
                  {question}
                </Typography>
              </Box>
            )}
            {answers.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {answers.map((answer, index) => {
                  const answerText = answer.answer || answer.text || '';
                  const isCorrect = answer.outcome === 'good answer' || answer.is_correct;
                  
                  return (
                    <Box
                      key={index}
                      sx={{
                        mb: 1.5,
                        p: 2,
                        borderRadius: '12px',
                        backgroundColor: blockType === 'quiz' && isCorrect 
                          ? '#E8F5E9' // Light green background
                          : '#F5F5F5', // Light gray background
                        border: blockType === 'quiz' && isCorrect 
                          ? '2px solid #4CAF50' 
                          : '1px solid #E0E0E0',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Typography variant="body1" sx={{ 
                        color: blockType === 'quiz' && isCorrect ? '#2E7D32' : '#34495E',
                        fontWeight: blockType === 'quiz' && isCorrect ? 'bold' : 400,
                        fontSize: '0.9rem'
                      }}>
                        {String.fromCharCode(65 + index)}. {answerText}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      }
      
      case 'teacher_notes': {
        const notes = block.bloc_data?.notes || block.content?.notes || block.layers || [];
        
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Show "Note animateur:" only once at the top */}
            <Typography variant="h6" sx={{ 
              fontWeight: 'bold', 
              mb: 1, 
              color: '#FF6B35', // Warm orange/coral
              fontSize: '1.1rem'
            }}>
              Note animateur:
            </Typography>
            
            {notes.map((note, index) => {
              const noteData = note.title ? note : {
                title: note.title || `Note ${index + 1}`,
                content: { text: note.content || note.text || '' }
              };
              
              const contentText = typeof noteData.content === 'string' 
                ? noteData.content 
                : noteData.content?.text || '';
              
              return (
                <Box key={index} sx={{ mb: 2 }}>
                  {noteData.title && (
                    <Typography variant="h6" sx={{ 
                      fontWeight: 'bold', 
                      mb: 1, 
                      color: '#2C3E50', // Dark blue-gray
                      fontSize: '1.125rem'
                    }}>
                      {noteData.title}
                    </Typography>
                  )}
                  <Typography variant="body1" sx={{ 
                    color: '#34495E', // Dark text
                    lineHeight: 1.8, 
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.9rem'
                  }}>
                    {contentText}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        );
      }
      
      case 'image': {
        const notes = block.bloc_data?.notes || block.content?.notes || block.layers || [];
        
        if (notes.length === 0) {
          return (
            <Typography variant="body1" sx={{ color: '#95a5a6', fontStyle: 'italic' }}>
              Aucun contenu textuel disponible
            </Typography>
          );
        }
        
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Show "Note animateur:" only once at the top */}
            <Typography variant="h6" sx={{ 
              fontWeight: 'bold', 
              mb: 1, 
              color: '#FF6B35', // Warm orange/coral
              fontSize: '1.1rem'
            }}>
              Note animateur:
            </Typography>
            
            {notes.map((note, index) => {
              const noteData = note.title ? note : {
                title: note.title || `Note ${index + 1}`,
                content: { text: note.content || note.text || '' }
              };
              
              const contentText = typeof noteData.content === 'string' 
                ? noteData.content 
                : noteData.content?.text || '';
              
              return (
                <Box key={index} sx={{ mb: 2 }}>
                  {noteData.title && (
                    <Typography variant="h6" sx={{ 
                      fontWeight: 'bold', 
                      mb: 1, 
                      color: '#2C3E50', // Dark blue-gray
                      fontSize: '1.125rem'
                    }}>
                      {noteData.title}
                    </Typography>
                  )}
                  <Typography variant="body1" sx={{ 
                    color: '#34495E', // Dark text
                    lineHeight: 1.8, 
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.9rem'
                  }}>
                    {contentText}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        );
      }
      
      case 'music': {
        const musicData = block.bloc_data || {};
        const musicUrl = musicData.url || '';
        const musicName = musicData.name || 'Musique';
        
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 'bold', 
              mb: 1, 
              color: '#FF6B35', // Warm orange/coral
              fontSize: '1.1rem'
            }}>
              {musicName}
            </Typography>
            {musicUrl ? (
              <Box sx={{ width: '100%', maxWidth: '500px' }}>
                <audio 
                  controls 
                  style={{ width: '100%' }}
                  src={musicUrl}
                >
                  Votre navigateur ne supporte pas l'élément audio.
                </audio>
              </Box>
            ) : (
              <Typography variant="body1" sx={{ color: '#95a5a6', fontStyle: 'italic' }}>
                Aucune musique disponible
              </Typography>
            )}
          </Box>
        );
      }
      
      default:
        return (
          <Typography variant="body1" sx={{ color: '#7f8c8d' }}>
            Type de bloc non supporté: {blockType}
          </Typography>
        );
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%)' // Warm yellow/cream gradient
      }}>
        <CircularProgress size={60} sx={{ color: '#FF6B35' }} />
      </Box>
    );
  }

  if (!seance || slides.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%)' // Warm yellow/cream gradient
      }}>
        <Typography variant="h5" sx={{ color: '#E65100', fontWeight: 'bold' }}>
          Aucune diapositive disponible
        </Typography>
      </Box>
    );
  }

  const currentBlock = slides[currentSlide]?.block;
  const currentSequenceTitle = slides[currentSlide]?.sequenceTitle || '';
  const imageUrl = getFirstValidatedImage(currentBlock);
  const content = getBlockContent(currentBlock);
  
  // Get next slide info for footer
  const getNextSlideInfo = () => {
    if (currentSlide >= slides.length - 1) {
      return 'Dernière slide de la présentation';
    }
    
    const nextBlock = slides[currentSlide + 1]?.block;
    const nextSequenceTitle = slides[currentSlide + 1]?.sequenceTitle || '';
    const nextBlockType = nextBlock?.type_bloc || '';
    
    let nextType = '';
    if (nextBlockType === 'quiz' || nextBlockType === 'sondage') {
      nextType = 'Quiz';
    } else if (nextBlockType === 'teacher_notes') {
      nextType = 'Note animateur';
    } else {
      nextType = 'Suite';
    }
    
    // Check if next slide is in same sequence
    if (nextSequenceTitle === currentSequenceTitle) {
      return `Slide suivant : ${nextSequenceTitle} - ${nextType}`;
    } else {
      // Extract sequence number from title
      const sequenceMatch = nextSequenceTitle.match(/Séquence (\d+)/);
      const sequenceNumber = sequenceMatch ? sequenceMatch[1] : '';
      return `Slide suivant : Séquence ${sequenceNumber} - ${nextType}`;
    }
  };

  return (
    <Box 
      className="presentation-container"
      sx={{ 
        width: '100vw', 
        height: '100vh', 
        background: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%)', // Warm yellow/cream gradient
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 9999
      }}
    >
      {/* Header with sequence title */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #FFE0B2 0%, #FFCC80 100%)', // Warm orange/peach gradient
        p: { xs: 1.5, md: 2 },
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '2px solid rgba(255, 152, 0, 0.2)',
        borderRadius: { xs: '0 0 20px 20px', md: '0' },
        boxShadow: '0 2px 8px rgba(255, 152, 0, 0.15)'
      }}>
        <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
          fontWeight: 'bold', 
          color: '#E65100', // Deep orange
          px: { xs: 1, md: 0 },
          lineHeight: 1.2
        }}>
          {currentSequenceTitle || seance.title || 'Présentation'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" sx={{ 
            color: '#BF360C', 
            fontWeight: 600,
            px: { xs: 1, md: 0 },
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            px: 1.5,
            py: 0.5,
            borderRadius: '12px'
          }}>
            {currentSlide + 1} / {slides.length}
          </Typography>
          <IconButton 
            onClick={toggleFullscreen} 
            sx={{ 
              color: '#E65100',
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': { 
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease',
              borderRadius: '12px'
            }}
          >
            {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        </Box>
      </Box>

      {/* Main slide content - scrollable container */}
      <Box 
        ref={slideContentRef}
        sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: imageUrl ? { xs: 'column', md: 'row' } : 'column',
          p: { xs: 2, md: 4 },
          gap: imageUrl ? { xs: 2, md: 4 } : 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          alignItems: imageUrl ? { xs: 'stretch', md: 'flex-start' } : 'center',
          justifyContent: imageUrl ? 'flex-start' : 'center',
          WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
          '-webkit-overflow-scrolling': 'touch',
          transition: 'opacity 0.3s ease',
          scrollBehavior: 'smooth'
        }}
      >
        {/* Image section - only show if image exists */}
        {imageUrl && (
          <Box sx={{ 
            flex: { xs: '0 0 auto', md: '0 0 45%' },
            width: { xs: '100%', md: 'auto' },
            minHeight: { xs: '40vh', md: 'auto' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #F5F5F5 0%, #EEEEEE 100%)', // Warm gray gradient
            borderRadius: { xs: '20px', md: '20px' },
            p: { xs: 2, md: 3 },
            flexShrink: 0,
            order: { xs: 1, md: 1 },
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
          }}>
            <img 
              src={imageUrl} 
              alt="Slide content"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
            />
          </Box>
        )}

        {/* Content section - warm beige background, centered when no image */}
        <Box 
          sx={{ 
            flex: imageUrl ? { xs: '0 0 auto', md: '1 1 55%' } : { xs: '1 1 auto', md: '1 1 auto' },
            width: { xs: '100%', md: imageUrl ? 'auto' : '80%' },
            maxWidth: imageUrl ? 'none' : { xs: '100%', md: '900px' },
            background: 'linear-gradient(135deg, #FFF9E6 0%, #FFE5B4 100%)', // Warm beige/cream gradient
            borderRadius: { xs: '20px', md: '20px' },
            border: '2px solid rgba(255, 183, 77, 0.3)',
            p: { xs: 2.5, md: 3.5 },
            order: { xs: 2, md: 2 },
            boxShadow: '0 4px 12px rgba(255, 183, 77, 0.15)',
            position: 'relative',
            mx: imageUrl ? 0 : 'auto'
          }}
        >
          {content}
          
          {/* Next slide info inside the content box */}
          {currentSlide < slides.length - 1 && (
            <Box sx={{
              mt: 3,
              pt: 2,
              borderTop: '2px solid rgba(255, 107, 53, 0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Box sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: '#FF6B35',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Typography sx={{ color: '#FFFFFF', fontSize: '0.75rem', fontWeight: 'bold' }}>
                  i
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ 
                color: '#FF6B35', 
                fontWeight: 'bold',
                fontSize: '0.875rem'
              }}>
                {getNextSlideInfo()}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Footer with navigation */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #FFE0B2 0%, #FFCC80 100%)', // Warm orange/peach gradient
        borderTop: '2px solid rgba(255, 152, 0, 0.2)',
        borderRadius: { xs: '20px 20px 0 0', md: '0' },
        p: { xs: 1.5, md: 2 },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxShadow: '0 -2px 8px rgba(255, 152, 0, 0.15)'
      }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <IconButton 
            onClick={handlePrevious}
            disabled={currentSlide === 0}
            sx={{ 
              color: currentSlide === 0 ? '#CCCCCC' : '#E65100',
              backgroundColor: currentSlide === 0 ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.4)',
              '&:hover': { 
                backgroundColor: currentSlide === 0 ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.6)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.3s ease',
              borderRadius: '16px',
              boxShadow: currentSlide === 0 ? 'none' : '0 2px 8px rgba(230, 81, 0, 0.2)'
            }}
          >
            <KeyboardArrowLeft sx={{ fontSize: { xs: 28, md: 32 } }} />
          </IconButton>
          
          <IconButton 
            onClick={handleNext}
            disabled={currentSlide === slides.length - 1}
            sx={{ 
              color: currentSlide === slides.length - 1 ? '#CCCCCC' : '#E65100',
              backgroundColor: currentSlide === slides.length - 1 ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.4)',
              '&:hover': { 
                backgroundColor: currentSlide === slides.length - 1 ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.6)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.3s ease',
              borderRadius: '16px',
              boxShadow: currentSlide === slides.length - 1 ? 'none' : '0 2px 8px rgba(230, 81, 0, 0.2)'
            }}
          >
            <KeyboardArrowRight sx={{ fontSize: { xs: 28, md: 32 } }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

export default SeancePresentation;

